import { useMemo } from 'react';

import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useGetRestaurantsQuery } from '@fhs-legacy/frontend/src/generated/sanity-graphql';

interface StoreDetailsRestaurant {
  restaurant?: IRestaurantNode;
  loading: boolean;
}

/**
 * Resolves the store behind the `storeId` param, for the details pane and for the map: a store
 * arrived at by link is often nowhere near the guest, so it is the only thing that can tell the
 * map where to open.
 *
 * Any store the guest can open details for from a card is already in the locator's own results,
 * so the common path costs nothing. The query only runs for a store we have never loaded —
 * a redirect from the old details URL, or a bookmarked locator link.
 */
export function useStoreDetailsRestaurant(
  storeId: string | undefined,
  knownRestaurants: IRestaurantNode[]
): StoreDetailsRestaurant {
  const knownRestaurant = useMemo(
    () => (storeId ? knownRestaurants.find(restaurant => restaurant._id === storeId) : undefined),
    [knownRestaurants, storeId]
  );

  const { data, loading } = useGetRestaurantsQuery({
    skip: !storeId || Boolean(knownRestaurant),
    variables: { filter: { _id: storeId }, limit: 1 },
  });

  // The Sanity restaurant carries every field the panel reads — hours, address, phone,
  // coordinates and the service-mode capability flags — so it stands in for the locator node.
  const fetchedRestaurant = data?.allRestaurants?.[0] as IRestaurantNode | undefined;

  // A result outlives the variables it was fetched with, so a store only counts while it is
  // the one being asked for — otherwise closing the pane, or opening another store, would
  // hand back the store before it.
  const restaurant =
    knownRestaurant ?? (fetchedRestaurant?._id === storeId ? fetchedRestaurant : undefined);

  return {
    restaurant,
    loading: Boolean(storeId) && !restaurant && loading,
  };
}
